/*
 * The storefront's one door to the server.
 *
 * WHY /api/method/ AND NEVER /api/v2
 * ----------------------------------
 * Every guest-reachable endpoint in `splashbox.api` stacks two `frappe.rate_limit`
 * decorators, and the limiter buckets on `frappe.form_dict.cmd`
 * (frappe/rate_limiter.py). Only the v1 route sets that key —
 * `frappe/api/v1.py:handle_rpc_call` does `frappe.form_dict.cmd = method` before
 * dispatching, and the v2 router does not. Calling /api/v2 would therefore silently
 * disable every rate limit this app relies on, so the base is hard-coded here and
 * nothing else in the bundle builds an endpoint URL.
 * See docs/BUILD_DECISIONS.md section 3.
 *
 * WHY THE ENVELOPE PARSER IS TOLERANT OF THREE SHAPES
 * ---------------------------------------------------
 * `splashbox.api._envelope` promises `{ok, data, error}`, and most endpoints keep
 * that promise through `ok()` / `fail()`. Two do not, on purpose:
 *   * `pricing.quote()` returns the ledger FLAT with its own `ok: true` at the top
 *     level (BUILD_DECISIONS section 4 spells the ledger out as top-level keys, and
 *     `@storefront` does not reshape a success);
 *   * `pricing.lookup_privilege_card()` returns `{valid, card_type, percent,
 *     message}` with no `ok` key at all.
 * A parser that insisted on one shape would read the quote as an empty payload and
 * price the sticky bar at nothing. So: `ok === false` is the only failure signal,
 * `data` is unwrapped when the key exists, and anything else is handed back whole.
 *
 * WHY SERVER TEXT IS ONLY TRUSTED WHEN IT CAME OUT OF OUR OWN ENVELOPE
 * -------------------------------------------------------------------
 * `error.message` inside our envelope is copy this app wrote for a customer to read
 * (`StorefrontError`, and the generic sentence for everything else). Frappe's own
 * error bodies are not: `exc_type`, `_server_messages` and, in developer_mode, a
 * traceback. So a non-envelope failure is answered with one of the sentences in
 * `COPY` below and the real reason is left in the Error Log where it belongs — the
 * same reasoning as `_envelope.GENERIC_ERROR_MESSAGE`, applied on this side of the
 * wire.
 *
 * NOTHING HERE TOUCHES THE DOM. Callers render with `textContent` /
 * `document.createElement`, never `innerHTML` (BUILD_DECISIONS section 10).
 */

/** The v1 RPC route. Read the module docstring before changing this. */
const ENDPOINT = "/api/method/";

/**
 * A phone in a shop doorway on one bar of 4G is the design case for this whole
 * storefront, so a hung request must not leave a control spinning forever: every
 * call is bounded and the caller falls back to the server-rendered path.
 */
const DEFAULT_TIMEOUT = 15000;

/**
 * What a customer is told when the failure did NOT come with copy of its own.
 * These are the bundle's only hard-coded sentences about failure; everything else a
 * customer reads about an error is written by `splashbox.api`.
 */
export const COPY = {
	generic: "Something went wrong at our end. Please try again in a moment.",
	offline: "That did not reach us. Check your connection and try again.",
	busy: "That was a lot of taps — please wait a moment and try again.",
	signin: "Please sign in to do that.",
	notFound: "We could not find that."
};

/** HTTP status -> the code and copy we answer with when there is no envelope. */
const STATUS_CODES = {
	400: ["invalid_input", COPY.generic],
	401: ["forbidden", COPY.signin],
	403: ["forbidden", COPY.signin],
	404: ["not_found", COPY.notFound],
	417: ["internal", COPY.generic],
	429: ["rate_limited", COPY.busy],
	503: ["unavailable", COPY.generic]
};

/**
 * A failure a caller can branch on.
 *
 * `code` is the machine half of the contract (`forbidden`, `not_found`,
 * `rate_limited`, `timeout`, `network`, `internal`…) and `message` is the half a
 * customer may be shown. `fromEnvelope` records which of the two kinds of failure
 * this was, because only an envelope's message is copy we wrote.
 */
export class ApiError extends Error {
	constructor(code, message, options) {
		super(message || COPY.generic);
		const extra = options || {};
		this.name = "ApiError";
		this.code = code || "internal";
		this.status = extra.status || 0;
		this.fromEnvelope = Boolean(extra.fromEnvelope);
		this.retryAfter = extra.retryAfter || 0;
	}
}

/**
 * The CSRF token frappe writes into the page.
 *
 * Read at CALL time and not at module load: `add_csrf_token` substitutes the
 * `<!-- csrf_token -->` comment that sits AFTER `{% block script %}`
 * (frappe/website/page_renderers/base_template_page.py:18 and
 * frappe/templates/base.html), so this bundle has already run by the time the token
 * exists. Caching it at import would send an empty header on every POST and every
 * signed-in customer would be answered with "Invalid Request".
 *
 * Guests get a token too, and frappe compares it for every unsafe method
 * (frappe/auth.py:80), so it is sent whether or not anyone is signed in.
 */
function csrfToken() {
	try {
		return (window.frappe && window.frappe.csrf_token) || "";
	} catch (error) {
		return "";
	}
}

/**
 * Query-string form of an argument object.
 *
 * Arrays and objects are JSON-encoded because that is what the Python side reads
 * back: `pricing._as_list` and `bag._item_codes` both accept a JSON string, and a
 * repeated key would be flattened to its first value anyway
 * (`frappe/app.py:299 make_form_dict` folds werkzeug's MultiDict with `dict.update`).
 */
function queryString(args) {
	const params = new URLSearchParams();
	Object.keys(args || {}).forEach(function (key) {
		const value = args[key];
		if (value === null || value === undefined) return;
		params.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
	});
	const query = params.toString();
	return query ? "?" + query : "";
}

/** Parse a body that may be JSON, may be frappe's plain-text 429, may be empty. */
function parseBody(text) {
	if (!text) return null;
	try {
		return JSON.parse(text);
	} catch (error) {
		return null;
	}
}

/**
 * Unwrap `{ok, data, error}` — see the module docstring for the two endpoints that
 * answer in a different shape and why this must tolerate them.
 */
function unwrap(body, response) {
	const payload = body && typeof body === "object" && "message" in body ? body.message : body;

	if (payload && typeof payload === "object" && payload.ok === false) {
		const error = payload.error || {};
		throw new ApiError(error.code, error.message, {
			status: response.status,
			fromEnvelope: true
		});
	}

	if (!response.ok) {
		throw httpError(response);
	}

	if (payload && typeof payload === "object" && payload.ok === true && "data" in payload) {
		return payload.data;
	}

	return payload;
}

/** A failure that never reached our envelope: answer it in our own words. */
function httpError(response) {
	const known = STATUS_CODES[response.status] || ["internal", COPY.generic];
	let retryAfter = 0;
	try {
		retryAfter = parseInt(response.headers.get("Retry-After"), 10) || 0;
	} catch (error) {
		retryAfter = 0;
	}
	return new ApiError(known[0], known[1], { status: response.status, retryAfter: retryAfter });
}

/**
 * Call one whitelisted method and return its payload.
 *
 * `method` is the dotted path (`splashbox.api.pricing.quote`). `args` are the
 * function's own keyword arguments — never a price, never an identity, never a
 * discount (BUILD_DECISIONS sections 1 and 11); the server re-reads all of those.
 *
 * POST sends a JSON body, which is the only way a real list survives the trip:
 * `frappe/app.py:make_form_dict` parses a JSON request straight into `form_dict`, so
 * `removed: ["ITEM-1", "ITEM-2"]` arrives as a Python list instead of as its first
 * element. `cmd` is still set by the v1 router afterwards, so both rate limiters
 * still run.
 *
 * Rejects with an `ApiError`. It never renders anything and never navigates: what a
 * failure means is the caller's decision, and on this storefront the answer is
 * almost always "fall back to the server-rendered form that is already on the page".
 */
export function call(method, args, options) {
	const settings = options || {};
	const verb = (settings.method || "POST").toUpperCase();
	const timeout = settings.timeout || DEFAULT_TIMEOUT;
	const controller = typeof AbortController === "function" ? new AbortController() : null;
	const signal = settings.signal || (controller ? controller.signal : undefined);

	let url = ENDPOINT + encodeURI(method);
	const headers = { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" };
	const init = { method: verb, headers: headers, credentials: "same-origin", signal: signal };

	if (verb === "GET") {
		url += queryString(args);
	} else {
		headers["Content-Type"] = "application/json";
		const token = csrfToken();
		if (token) headers["X-Frappe-CSRF-Token"] = token;
		init.body = JSON.stringify(args || {});
	}

	let timer = null;
	if (controller && timeout > 0) {
		timer = setTimeout(function () {
			controller.abort();
		}, timeout);
	}

	// `Promise.prototype.finally` is deliberately not used: esbuild is told to emit
	// es2017 (frappe/esbuild/esbuild.js) and transpiles syntax only, so a runtime
	// method added in es2018 would ship unguarded to whatever browser a customer has.
	function clear() {
		if (timer) clearTimeout(timer);
		timer = null;
	}

	return fetch(url, init)
		.then(function (response) {
			return response.text().then(function (text) {
				return unwrap(parseBody(text), response);
			});
		})
		.then(
			function (value) {
				clear();
				return value;
			},
			function (error) {
				clear();
				if (error instanceof ApiError) throw error;
				// An abort is either our own timeout or a caller superseding this
				// request; both are "no answer", and neither is worth a sentence on
				// the screen.
				if (error && error.name === "AbortError") {
					throw new ApiError("timeout", COPY.offline);
				}
				throw new ApiError("network", COPY.offline);
			}
		);
}

/** `call` with the verb spelled out, for the two GET endpoints the bundle uses. */
export function get(method, args, options) {
	return call(method, args, Object.assign({}, options, { method: "GET" }));
}

/** `call` with the verb spelled out. Everything that changes anything is a POST. */
export function post(method, args, options) {
	return call(method, args, Object.assign({}, options, { method: "POST" }));
}

/**
 * The endpoints this bundle is allowed to reach, named once.
 *
 * A map rather than string literals scattered through five modules: a renamed
 * Python function should break the build's grep in one place, and nothing outside
 * this file should be able to invent a `cmd` the rate limiters have never seen.
 */
export const METHODS = {
	// The card is NOT looked up on its own: `pricing.quote` resolves the number and
	// returns the verdict with the ledger, so the total and the "5% applied" line can
	// never come from two different answers.
	quote: "splashbox.api.pricing.quote",
	requestOtp: "splashbox.api.auth.request_otp",
	resendOtp: "splashbox.api.auth.resend_otp",
	verifyOtp: "splashbox.api.auth.verify_otp",
	logout: "splashbox.api.auth.logout",
	addWatch: "splashbox.api.watch.add_watch",
	removeWatch: "splashbox.api.watch.remove_watch"
};
